// src/components/superadmin/Dashboard.tsx
"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { api } from "../../services/Axios"
import PlanSelectorModal from './PlanSelectorModal'
import OrganizationEditModal from './OrganizationEditModal'
import {
  Search,
  Filter,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  Building,
  RotateCw,
  Edit,
  Users,
} from "lucide-react"
import MessageModal from "../utils/avisos/MessageModal"
import ConfirmationModal from "../utils/avisos/ConfirmationModal"
import PaymentProofModal from "./PaymentProofModal"

interface Organization {
  _id: string
  name: string
  razonSocial: string
  NIT: string
  direccion: string
  ciudad: string
  responsableIVA: boolean
  createdAt: string
  telefonoContacto?: string
  identificador?: string
  pin?: string
  setId?: string
  admin: {
    nombre: string
    email: string
  }
  stats: {
    products: number
    sales: number
    vendedores: number
    expenses: number
    totalCajas: number
  }
  subscription: {
    _id: string
    plan: string
    status: string
    startDate: string
    endDate: string
    paymentMethod: string
    paymentProof: string | null
  } | null
}

const Dashboard: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [filteredOrganizations, setFilteredOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [message, setMessage] = useState({ text: "", type: "" })
  const [showMessage, setShowMessage] = useState(false)
  const [confirmAction, setConfirmAction] = useState<{ action: string; orgId: string; orgName?: string } | null>(null)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [selectedPaymentProof, setSelectedPaymentProof] = useState<string | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const [showPlanSelector, setShowPlanSelector] = useState(false)
  const [selectedOrgForPlan, setSelectedOrgForPlan] = useState<{id: string, name: string} | null>(null)
  
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedOrgForEdit, setSelectedOrgForEdit] = useState<Organization | null>(null)

  const navigate = useNavigate()

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true)
        const response = await api.get("/api/admin/organizations")
        setOrganizations(response.data)
        setFilteredOrganizations(response.data)
        setLoading(false)
      } catch (error) {
        console.error("Error al obtener organizaciones:", error)
        setMessage({ text: "Error al cargar organizaciones", type: "error" })
        setShowMessage(true)
        setLoading(false)
      }
    }

    fetchOrganizations()
  }, [refreshTrigger])

  useEffect(() => {
    let result = [...organizations]

    if (searchTerm) {
      result = result.filter(
        (org) =>
          org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          org.NIT.includes(searchTerm) ||
          org.admin?.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
          org.admin?.email.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    if (filterStatus !== "all") {
      if (filterStatus === "active") {
        result = result.filter((org) => org.subscription?.status === "active")
      } else if (filterStatus === "inactive") {
        result = result.filter((org) => !org.subscription || org.subscription.status !== "active")
      }
    }

    setFilteredOrganizations(result)
    setCurrentPage(1) 
  }, [searchTerm, filterStatus, organizations])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterStatus(e.target.value)
  }

  const handleViewDetails = (orgId: string) => {
    navigate(`/superadmin/organization/${orgId}`)
  }

  const handleEditOrganization = (org: Organization) => {
    setSelectedOrgForEdit(org)
    setShowEditModal(true)
  }

  const handleSaveOrganization = async (orgId: string, data: Partial<Organization>) => {
    try {
      await api.put(`/api/admin/organization/${orgId}`, data)
      setMessage({ text: "Organización actualizada exitosamente", type: "success" })
      setShowMessage(true)
      setShowEditModal(false)
      setRefreshTrigger((prev) => prev + 1)
    } catch (error: any) {
      console.error("Error al actualizar organización:", error)
      setMessage({ 
        text: error.response?.data?.msg || "Error al actualizar la organización", 
        type: "error" 
      })
      setShowMessage(true)
    }
  }

  const handleActivateSubscription = (orgId: string) => {
    const org = organizations.find((o) => o._id === orgId)
    if (org) {
      setSelectedOrgForPlan({ id: orgId, name: org.name })
      setShowPlanSelector(true)
    }
  }

  const handleConfirmActivateWithPlan = async (plan: string) => {
    if (!selectedOrgForPlan) return

    try {
      await api.post(`/api/admin/subscription/activate/${selectedOrgForPlan.id}`, {
        plan: plan,
        months: 1,
        paymentMethod: "manual",
      })
      setMessage({ text: "Suscripción activada exitosamente", type: "success" })
      setShowMessage(true)
      setShowPlanSelector(false)
      setSelectedOrgForPlan(null)
      setRefreshTrigger((prev) => prev + 1)
    } catch (error) {
      console.error("Error al activar suscripción:", error)
      setMessage({ text: "Error al activar la suscripción", type: "error" })
      setShowMessage(true)
      setShowPlanSelector(false)
    }
  }

  const handleDeactivateSubscription = (orgId: string) => {
    const org = organizations.find((o) => o._id === orgId)
    const nombreOrg = org?.name || "la organización"
    const clave = prompt(`⚠️ Vas a desactivar la suscripción de *${nombreOrg}*. Ingresa la clave de seguridad:`)
  
    if (clave !== "admin123") {
      alert("Clave incorrecta. Operación cancelada.")
      return
    }
  
    setConfirmAction({ action: "deactivate", orgId, orgName: nombreOrg })
    setShowConfirmation(true)
  }  

  const handleResetOrganization = (orgId: string) => {
    const org = organizations.find((o) => o._id === orgId)
    const nombreOrg = org?.name || "la organización"
    const clave = prompt(`⚠️ Esta acción eliminará todos los datos de *${nombreOrg}*. Ingresa la clave de seguridad:`)
  
    if (clave !== "admin123") {
      alert("Clave incorrecta. Operación cancelada.")
      return
    }
  
    setConfirmAction({ action: "reset", orgId, orgName: nombreOrg })
    setShowConfirmation(true)
  } 

  const totalPages = Math.ceil(filteredOrganizations.length / itemsPerPage)
  const paginatedOrganizations = filteredOrganizations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const handleConfirmAction = async () => {
    if (!confirmAction) return

    try {
      const { action, orgId } = confirmAction

      if (action === "activate") {
        await api.post(`/api/admin/subscription/activate/${orgId}`, {
          months: 1,
          paymentMethod: "manual",
        })
        setMessage({ text: "Suscripción activada exitosamente", type: "success" })
      } else if (action === "deactivate") {
        await api.post(`/api/admin/subscription/deactivate/${orgId}`)
        setMessage({ text: "Suscripción desactivada exitosamente", type: "success" })
      } else if (action === "reset") {
        await api.delete(`/api/admin/organization/reset/${orgId}`)
        setMessage({ text: "Datos de la organización eliminados exitosamente", type: "success" })
      }

      setShowMessage(true)
      setShowConfirmation(false)
      setRefreshTrigger((prev) => prev + 1)
    } catch (error) {
      console.error("Error al realizar la acción:", error)
      setMessage({ text: "Error al realizar la acción", type: "error" })
      setShowMessage(true)
      setShowConfirmation(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
            <Building className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Panel de Administración</h1>
        </div>
        <p className="text-gray-600 ml-14">Gestiona todas las organizaciones y suscripciones</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Total Organizaciones</p>
              <p className="text-3xl font-bold text-gray-900">{organizations.length}</p>
              <p className="text-xs text-gray-400 mt-2">Registradas en el sistema</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-md">
              <Building className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Suscripciones Activas</p>
              <p className="text-3xl font-bold text-green-600">
                {organizations.filter((org) => org.subscription?.status === "active").length}
              </p>
              <p className="text-xs text-gray-400 mt-2">Clientes con acceso</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-md">
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Suscripciones Inactivas</p>
              <p className="text-3xl font-bold text-red-600">
                {organizations.filter((org) => !org.subscription || org.subscription.status !== "active").length}
              </p>
              <p className="text-xs text-gray-400 mt-2">Requieren atención</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl shadow-md">
              <XCircle className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-100">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Buscar por nombre, NIT, administrador..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-colors duration-200"
            />
          </div>

          <div className="relative w-full lg:w-64">
            <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
            <select
              value={filterStatus}
              onChange={handleFilterChange}
              className="w-full pl-12 pr-10 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none appearance-none transition-colors duration-200 cursor-pointer"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Suscripción activa</option>
              <option value="inactive">Suscripción inactiva</option>
            </select>
          </div>

          <button
            onClick={() => setRefreshTrigger((prev) => prev + 1)}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 font-medium"
          >
            <RotateCw className="h-5 w-5" />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* Organizations Table */}
      {loading ? (
        <div className="flex flex-col justify-center items-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mb-4"></div>
          <p className="text-gray-500 font-medium">Cargando organizaciones...</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Organización
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Administrador
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Estadísticas
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Suscripción
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedOrganizations.map((org) => (
                    <tr key={org._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                            <span className="text-white font-bold text-sm">
                              {org.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-gray-900 truncate">{org.name}</p>
                            <p className="text-xs text-gray-500">NIT: {org.NIT}</p>
                            <p className="text-xs text-gray-400">{org.ciudad} • {formatDate(org.createdAt)}</p>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{org.admin?.nombre || "Sin administrador"}</p>
                            <p className="text-xs text-gray-500 truncate">{org.admin?.email || "—"}</p>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span className="text-gray-700">{org.stats.products} Productos</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-gray-700">{org.stats.sales} Ventas</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                            <span className="text-gray-700">{org.stats.totalCajas} Cajas</span>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        {org.subscription?.status === "active" ? (
                          <div>
                            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-2">
                              <CheckCircle className="w-3.5 h-3.5" />
                              Activa
                            </div>
                            <p className="text-xs text-gray-600 font-medium">{org.subscription.plan}</p>
                            <p className="text-xs text-gray-400">Hasta: {formatDate(org.subscription.endDate)}</p>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                            <XCircle className="w-3.5 h-3.5" />
                            Inactiva
                          </div>
                        )}
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors duration-200 group relative"
                            title="Ver Detalles"
                            onClick={() => handleViewDetails(org._id)}
                          >
                            <Eye className="w-4 h-4" />
                            <span className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                              Ver detalles
                            </span>
                          </button>
                          <button
                            className="p-2 bg-purple-100 hover:bg-purple-200 text-purple-600 rounded-lg transition-colors duration-200"
                            title="Editar"
                            onClick={() => handleEditOrganization(org)}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          {org.subscription?.status === "active" ? (
                            <button
                              className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors duration-200"
                              title="Desactivar"
                              onClick={() => handleDeactivateSubscription(org._id)}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              className="p-2 bg-green-100 hover:bg-green-200 text-green-600 rounded-lg transition-colors duration-200"
                              title="Activar"
                              onClick={() => handleActivateSubscription(org._id)}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            className="p-2 bg-amber-100 hover:bg-amber-200 text-amber-600 rounded-lg transition-colors duration-200"
                            title="Reset"
                            onClick={() => handleResetOrganization(org._id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <button
                className="px-4 py-2 rounded-xl bg-white border-2 border-gray-200 hover:border-blue-500 hover:text-blue-600 text-gray-700 font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Anterior
              </button>

              <div className="flex gap-2">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, index) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = index + 1;
                  } else if (currentPage <= 3) {
                    pageNum = index + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + index;
                  } else {
                    pageNum = currentPage - 2 + index;
                  }

                  return (
                    <button
                      key={pageNum}
                      className={`w-10 h-10 rounded-xl font-semibold transition-all duration-200 shadow-sm ${
                        currentPage === pageNum
                          ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md"
                          : "bg-white border-2 border-gray-200 hover:border-blue-500 hover:text-blue-600 text-gray-700"
                      }`}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                className="px-4 py-2 rounded-xl bg-white border-2 border-gray-200 hover:border-blue-500 hover:text-blue-600 text-gray-700 font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
          
      <MessageModal
        isOpen={showMessage}
        message={message.text}
        messageType={message.type as "success" | "error" | "warning" | "info"}
        onClose={() => setShowMessage(false)}
      />

      <ConfirmationModal
        isOpen={showConfirmation}
        title={
          confirmAction?.action === "activate"
            ? `Activar Suscripción - ${confirmAction?.orgName}`
            : confirmAction?.action === "deactivate"
              ? `Desactivar Suscripción - ${confirmAction?.orgName}`
              : `Eliminar Datos - ${confirmAction?.orgName}`
        }
        message={
          confirmAction?.action === "activate"
            ? `¿Estás seguro de que deseas activar la suscripción de **${confirmAction?.orgName}**?`
            : confirmAction?.action === "deactivate"
              ? `¿Estás seguro de que deseas desactivar la suscripción de **${confirmAction?.orgName}**?`
              : `¿Estás seguro de que deseas eliminar todos los datos de **${confirmAction?.orgName}**? Esta acción no se puede deshacer y solo mantendrá el usuario administrador y la información básica.`
        }
        onConfirm={handleConfirmAction}
        onCancel={() => setShowConfirmation(false)}
      />

      <PaymentProofModal
        isOpen={!!selectedPaymentProof}
        imageUrl={selectedPaymentProof || ""}
        onClose={() => setSelectedPaymentProof(null)}
      />

      <PlanSelectorModal
        isOpen={showPlanSelector}
        orgName={selectedOrgForPlan?.name || ""}
        onConfirm={handleConfirmActivateWithPlan}
        onCancel={() => {
          setShowPlanSelector(false)
          setSelectedOrgForPlan(null)
        }}
      />

      <OrganizationEditModal
        isOpen={showEditModal}
        organization={selectedOrgForEdit}
        onClose={() => {
          setShowEditModal(false)
          setSelectedOrgForEdit(null)
        }}
        onSave={handleSaveOrganization}
      />
    </div>
  )
}

export default Dashboard